/**
 * PRP-WEB-1 (W2): the project overview and the chat answers, read from the routes W1 adds
 * (`app/api/routes/project_overview.py`). Its own module like `mcpApi.ts`: the shared `api`
 * instance attaches the Firebase token, prefixes `/api/v1` and unwraps `response.data`.
 *
 * Both reads are normalised once here, so an older server (404) or an odd body never breaks the
 * project page: the caller gets empty lists, never a throw from a missing field.
 */
import { api } from './api';
import type { Project } from '../types';

/** Where an estimate came from (W1.2 `source`; the server sends chat or import). */
export type EstimateSource = 'chat' | 'import';

/** One estimate card on the Overview (W1.2 `estimates[]`). */
export interface OverviewEstimate {
  parse_hash: string;
  name: string;
  source: EstimateSource;
  /** A chat estimate linked to the project: its room quantities follow floor plan edits (DD4). */
  follows_plan: boolean;
  /** RCV; null while the estimate is not priced yet. */
  total: number | null;
  rooms: number;
  line_items: number;
  last_changed_at: string | null;
  /** `/completeness?import=<hash>&project=<id>`. */
  open_url: string;
}

/** GET /api/v1/projects/{id}/overview. */
export interface ProjectOverview {
  estimates: OverviewEstimate[];
  plan: { rooms: number; has_geometry: boolean; editable_rooms: number };
  open_questions: number;
  answered_in_chat: number;
}

/** A question the chat asked and the user answered (W1.4). */
export interface ChatAnswer {
  question: string;
  answer: string;
  answered_at: string | null;
}

/** A question still parked in the chat (W1.4). */
export interface ChatWaiting {
  question: string;
  options: string[];
  asked_at: string | null;
}

/** One estimate's chat answers (W1.4, grouped by estimate). */
export interface ChatAnswersGroup {
  parse_hash: string;
  name: string;
  answered: ChatAnswer[];
  waiting: ChatWaiting[];
}

type Raw = Record<string, unknown>;
const str = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));
const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
const arr = (v: unknown): Raw[] => (Array.isArray(v) ? (v.filter((x) => x && typeof x === 'object') as Raw[]) : []);
const when = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);

function sourceOf(v: unknown, hash: string): EstimateSource {
  if (v === 'chat' || v === 'import') return v;
  return hash.startsWith('mcp-') ? 'chat' : 'import';
}

/** The W1.2 body as the page uses it; anything missing becomes empty. */
export function normaliseOverview(raw: unknown, projectId: string): ProjectOverview {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Raw;
  const estimates = arr(r.estimates)
    .filter((e) => str(e.parse_hash))
    .map((e) => {
      const hash = str(e.parse_hash);
      return {
        parse_hash: hash,
        name: str(e.name).trim() || hash.slice(0, 12),
        source: sourceOf(e.source, hash),
        follows_plan: e.follows_plan === true,
        total: typeof e.total === 'number' && Number.isFinite(e.total) ? e.total : null,
        rooms: num(e.rooms),
        line_items: num(e.line_items),
        last_changed_at: when(e.last_changed_at),
        open_url:
          str(e.open_url) ||
          `/completeness?import=${encodeURIComponent(hash)}&project=${encodeURIComponent(projectId)}`,
      };
    });
  const plan = (r.plan && typeof r.plan === 'object' ? r.plan : {}) as Raw;
  const answered = r.answered_in_chat;
  return {
    estimates,
    plan: { rooms: num(plan.rooms), has_geometry: plan.has_geometry === true, editable_rooms: num(plan.editable_rooms) },
    open_questions: num(r.open_questions),
    answered_in_chat: Array.isArray(answered) ? answered.length : num(answered),
  };
}

function optionText(o: unknown): string {
  if (typeof o === 'string') return o;
  if (o && typeof o === 'object') {
    const r = o as Raw;
    return str(r.description ?? r.label ?? r.text ?? r.name);
  }
  return '';
}

/**
 * The W1.4 body grouped by estimate: `{project_id, estimates: [{parse_hash, name,
 * answered: [{proposal_id, question, answer, answered_at}], waiting: [{proposal_id, question,
 * options: [{choice, description}], asked_at, state}]}]}`, newest estimate first.
 * Also tolerates a flat `{answers: [{parse_hash, estimate_name, status, question, answer|options}]}`.
 */
export function normaliseChatAnswers(raw: unknown): ChatAnswersGroup[] {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Raw;
  const groups = new Map<string, ChatAnswersGroup>();
  const group = (hash: string, name: string) => {
    let g = groups.get(hash);
    if (!g) {
      g = { parse_hash: hash, name: name || hash.slice(0, 12), answered: [], waiting: [] };
      groups.set(hash, g);
    }
    return g;
  };
  const answerOf = (a: Raw): ChatAnswer => ({
    question: str(a.question),
    answer: str(a.answer ?? a.chosen ?? a.answer_text),
    answered_at: when(a.answered_at ?? a.decided_at ?? a.when),
  });
  const waitingOf = (a: Raw): ChatWaiting => ({
    question: str(a.question),
    options: (Array.isArray(a.options) ? a.options : []).map(optionText).filter(Boolean),
    asked_at: when(a.asked_at ?? a.created_at ?? a.when),
  });
  for (const e of arr(r.estimates)) {
    const g = group(str(e.parse_hash), str(e.name));
    g.answered.push(...arr(e.answered).map(answerOf).filter((a) => a.question));
    g.waiting.push(...arr(e.waiting ?? e.parked).map(waitingOf).filter((w) => w.question));
  }
  for (const a of arr(r.answers ?? r.items)) {
    const g = group(str(a.parse_hash ?? a.estimate_id), str(a.estimate_name ?? a.name));
    if (a.status === 'parked' || a.status === 'waiting') {
      const w = waitingOf(a);
      if (w.question) g.waiting.push(w);
    } else {
      const x = answerOf(a);
      if (x.question) g.answered.push(x);
    }
  }
  return [...groups.values()].filter((g) => g.parse_hash && (g.answered.length || g.waiting.length));
}

/** The time a project last changed, for the dashboard's Updated column (W1.1 with fallbacks). */
export function projectActivityAt(p: Pick<Project, 'last_activity_at' | 'updated_at' | 'created_at'>): string | null {
  return p.last_activity_at || p.updated_at || p.created_at || null;
}

export const ProjectOverviewAPI = {
  /** GET /api/v1/projects/{id}/overview (W1.2). */
  getOverview: (projectId: string): Promise<ProjectOverview> =>
    Promise.resolve()
      .then(() => api.get(`/projects/${encodeURIComponent(projectId)}/overview`) as Promise<unknown>)
      .then((raw) => normaliseOverview(raw, projectId)),
  /** GET /api/v1/projects/{id}/chat-answers (W1.4). */
  getChatAnswers: (projectId: string): Promise<ChatAnswersGroup[]> =>
    Promise.resolve()
      .then(() => api.get(`/projects/${encodeURIComponent(projectId)}/chat-answers`) as Promise<unknown>)
      .then(normaliseChatAnswers),
};
